import React, { useState } from 'react';
import { useEcran } from './useEcran';

/* =============================================================================
   DateFilter — selection de la periode analysee, trois modes :
     - Jour    : un jour precis, avec fleches pour avancer jour par jour
     - Mois    : un mois entier (du 1er au dernier jour, plafonne a aujourd'hui)
     - Periode : un intervalle libre debut -> fin, avec raccourcis

   Remonte toujours un couple { from, to } au format YYYY-MM-DD ; quand
   from === to l'API bascule d'elle-meme en mode jour unique.
   ============================================================================= */

const pad = (n) => String(n).padStart(2, '0');
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parse = (s) => new Date(`${s}T12:00:00`);
const decaler = (s, n) => { const d = parse(s); d.setDate(d.getDate() + n); return iso(d); };

/* Dernier jour du mois `ym` (YYYY-MM), sans depasser aujourd'hui. */
function bornesDuMois(ym, plafond) {
  const [y, m] = ym.split('-').map(Number);
  const fin = iso(new Date(y, m, 0));
  return [`${ym}-01`, fin > plafond ? plafond : fin];
}

const MODES = [
  { k: 'jour', label: 'Jour' },
  { k: 'mois', label: 'Mois' },
  { k: 'periode', label: 'Période' },
];

export default function DateFilter({ from, to, onChange }) {
  const aujourdhui = iso(new Date());
  const { estMobile } = useEcran();
  const [mode, setMode] = useState('jour');
  const [pFrom, setPFrom] = useState(from || decaler(aujourdhui, -6));
  const [pTo, setPTo] = useState(to || aujourdhui);

  const changerMode = (k) => {
    setMode(k);
    if (k === 'jour') {
      onChange(to, to);
    } else if (k === 'mois') {
      onChange(...bornesDuMois(to.slice(0, 7), aujourdhui));
    } else {
      onChange(pFrom, pTo);
    }
  };

  const appliquerPeriode = (f, t) => {
    setPFrom(f); setPTo(t);
    if (f && t) onChange(f > t ? t : f, f > t ? f : t);
  };

  const btnStyle = (actif) => ({
    border: 'none', borderRadius: 6,
    padding: estMobile ? '8px 12px' : '4px 10px', fontSize: estMobile ? '.8rem' : '.75rem',
    fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)',
    background: actif ? 'var(--surface)' : 'transparent',
    color: actif ? 'var(--text)' : 'var(--text3)',
    boxShadow: actif ? '0 1px 2px rgba(16,24,40,.10)' : 'none',
  });

  const fleche = {
    // Une fleche de 26x30 n'est pas atteignable au pouce.
    width: estMobile ? 38 : 26, height: estMobile ? 38 : 30,
    border: '1px solid var(--border2)', borderRadius: 'var(--r-sm)',
    background: 'var(--surface)', color: 'var(--text3)', cursor: 'pointer',
    fontSize: estMobile ? '1.1rem' : '.85rem', lineHeight: 1,
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  };

  /* 16px reels sur les champs de date : en dessous, iOS zoome tout seul a la
     mise au point et l'utilisateur se retrouve avec une page decalee. La regle
     CSS `.hdr-sel` a 480px les ramenait a .72rem, d'ou cette surcharge. */
  const champDate = estMobile
    ? { fontSize: 16, padding: '7px 8px', maxWidth: 'none', minWidth: 0 }
    : undefined;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', gap: 3, background: 'var(--surface2, #f1f3f5)', padding: 3, borderRadius: 8, border: '1px solid var(--border)' }}>
        {MODES.map((m) => (
          <button key={m.k} onClick={() => changerMode(m.k)} style={btnStyle(mode === m.k)}>{m.label}</button>
        ))}
      </div>

      {mode === 'jour' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: estMobile ? 6 : 4, flexWrap: 'wrap' }}>
          <button style={fleche} title="Jour précédent" onClick={() => onChange(decaler(to, -1), decaler(to, -1))}>‹</button>
          <input type="date" className="hdr-sel" style={champDate} max={aujourdhui} value={to}
            onChange={(e) => e.target.value && onChange(e.target.value, e.target.value)} />
          <button style={{ ...fleche, opacity: to >= aujourdhui ? .35 : 1 }} title="Jour suivant"
            disabled={to >= aujourdhui}
            onClick={() => onChange(decaler(to, 1), decaler(to, 1))}>›</button>
          {to !== aujourdhui && (
            <button className="hdr-sel" style={estMobile ? { fontSize: '.78rem', padding: '8px 10px' } : { fontSize: '.72rem' }}
              onClick={() => onChange(aujourdhui, aujourdhui)}>Aujourd&rsquo;hui</button>
          )}
        </div>
      )}

      {mode === 'mois' && (
        <input type="month" className="hdr-sel" style={champDate} max={aujourdhui.slice(0, 7)} value={to.slice(0, 7)}
          onChange={(e) => e.target.value && onChange(...bornesDuMois(e.target.value, aujourdhui))} />
      )}

      {mode === 'periode' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: estMobile ? 6 : 5, flexWrap: 'wrap' }}>
          <input type="date" className="hdr-sel" style={champDate} max={aujourdhui} value={pFrom}
            onChange={(e) => appliquerPeriode(e.target.value, pTo)} />
          <span style={{ color: 'var(--text4)', fontSize: '.8rem' }}>→</span>
          <input type="date" className="hdr-sel" style={champDate} max={aujourdhui} value={pTo}
            onChange={(e) => appliquerPeriode(pFrom, e.target.value)} />
          {[
            { l: '7 j', n: 7 },
            { l: '30 j', n: 30 },
          ].map((q) => (
            <button key={q.l} className="hdr-sel"
              style={estMobile ? { fontSize: '.78rem', padding: '8px 10px' } : { fontSize: '.72rem' }}
              onClick={() => appliquerPeriode(decaler(aujourdhui, -(q.n - 1)), aujourdhui)}>{q.l}</button>
          ))}
        </div>
      )}
    </div>
  );
}
